import { useEffect, useImperativeHandle, useState } from 'react';
import { useForm, useSelector } from '@tanstack/react-form';

import { useAuth } from '@nvision/identity-core';
import { Button, DateRangePickerField, InputField } from '@nvision/ui';

import {
  type Class,
  ClassField,
  type LaneRow,
  LaneSearchModal,
  type LinerType,
  LinerTypeField,
  type ProductRow,
  ProductSearchModal,
  type ShipType,
  ShipTypeField,
} from '@/modules/entities';

import { EMPTY_TARIFF_DETAILS_FILTERS, isTariffDetailsFilterSelected } from '../../../hooks';
import { type TariffDetailsFiltersForm, tariffDetailsFiltersFormPartialSchema } from '../../../models';

import styles from './tariff-details-filter-form.module.css';

export type TariffDetailsFilterFormFormRef = {
  submit: () => void;
  reset: () => void;
  canSubmit: () => boolean;
};

interface TariffDetailsFilterFormProps {
  defaultValues: TariffDetailsFiltersForm;
  formRef: React.RefObject<TariffDetailsFilterFormFormRef>;
  onApply: (filters: TariffDetailsFiltersForm) => void;
  onReset: () => void;
  onValuesChange: (filters: TariffDetailsFiltersForm) => void;
}

type SearchModalField = 'laneKey' | 'productDescription';

export const TariffDetailsFilterForm: React.FC<TariffDetailsFilterFormProps> = ({
  defaultValues,
  formRef,
  onApply,
  onReset,
  onValuesChange,
}) => {
  const [activeSearchModal, setActiveSearchModal] = useState<SearchModalField | null>(null);
  const auth = useAuth();
  const timeZone = auth.user?.profile.pref.timezone;

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      if (value) {
        onApply(value);
      }
    },
    validators: {
      onChange: tariffDetailsFiltersFormPartialSchema,
    },
  });

  const values = useSelector(form.store, (state) => state.values);

  useEffect(() => {
    onValuesChange(values);
  }, [onValuesChange, values]);

  const onLaneSelectionChange = (lane: LaneRow) => {
    form.setFieldValue('laneKey', {
      id: lane.laneKey,
      label: lane.laneCode || String(lane.laneKey),
    });
    setActiveSearchModal(null);
  };

  const onProductSelectionChange = (value: ProductRow) => {
    form.setFieldValue('productDescription', value.productDesc ?? '');
    setActiveSearchModal(null);
  };

  const openFieldSearchModal = (field: SearchModalField) => {
    setActiveSearchModal(field);
  };

  useImperativeHandle(formRef, () => ({
    canSubmit: () => form.state.canSubmit,
    reset: () => form.reset(EMPTY_TARIFF_DETAILS_FILTERS, { keepDefaultValues: false }),
    submit: () => {
      form.validate('submit');
      form.handleSubmit();
    },
  }));

  const handleSubmit = (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  const handleReset = () => {
    formRef.current?.reset();
    onReset();
  };

  return (
    <form onSubmit={handleSubmit} className={styles.filterForm}>
      <div className={styles.row}>
        <form.Field name="effectiveDate">
          {(field) => (
            <DateRangePickerField
              className={styles.field}
              label="Effective Date"
              size="sm"
              clearable
              timeZone={timeZone}
              value={field.state.value ?? null}
              onChange={(range) => {
                field.handleChange(range ?? undefined);
              }}
            />
          )}
        </form.Field>

        <form.Field name="laneKey">
          {(field) => (
            <InputField
              className={styles.field}
              label="Lane ID"
              size="sm"
              clearable
              readOnly
              placeholder="Select"
              value={field.state.value?.label ?? ''}
              onChange={() => field.handleChange(null)}
              onClick={() => openFieldSearchModal('laneKey')}
            />
          )}
        </form.Field>

        <form.Field name="productDescription">
          {(field) => (
            <InputField
              className={styles.field}
              label="Product Description"
              size="sm"
              clearable
              readOnly
              placeholder="Select"
              value={field.state.value ?? ''}
              onChange={() => field.handleChange('')}
              onClick={() => openFieldSearchModal('productDescription')}
            />
          )}
        </form.Field>
      </div>

      <div className={styles.actionsRow}>
        <form.Field name="class">
          {(field) => (
            <ClassField
              className={styles.field}
              label="Class"
              size="sm"
              clearable
              value={(field.state.value as unknown as Class | null) ?? null}
              onSelectionChange={(freightClass) =>
                field.handleChange(freightClass ? { id: String(freightClass.id), label: freightClass.label } : null)
              }
            />
          )}
        </form.Field>

        <form.Field name="accountNumber">
          {(field) => (
            <InputField
              className={styles.field}
              label="Account Number"
              size="sm"
              clearable
              placeholder="Enter"
              value={field.state.value ?? ''}
              onChange={(e) => field.handleChange(e.target.value)}
            />
          )}
        </form.Field>

        <form.Field name="shipmentType">
          {(field) => (
            <ShipTypeField
              className={styles.field}
              label="Shipment Type"
              size="sm"
              clearable
              value={(field.state.value as unknown as ShipType | null) ?? null}
              onSelectionChange={(shipType) =>
                field.handleChange(
                  shipType ? { id: String(shipType.codeId ?? shipType.id), label: shipType.label } : null,
                )
              }
            />
          )}
        </form.Field>

        <form.Field name="liner">
          {(field) => (
            <LinerTypeField
              className={styles.field}
              label="Liner"
              size="sm"
              clearable
              value={(field.state.value as unknown as LinerType | null) ?? null}
              onSelectionChange={(linerType) =>
                field.handleChange(
                  linerType ? { id: String(linerType.codeId ?? linerType.id), label: linerType.label } : null,
                )
              }
            />
          )}
        </form.Field>

        <form.Subscribe selector={(state) => state.values}>
          {(values) => {
            const hasFilters = Object.values(values).some(isTariffDetailsFilterSelected);

            return (
              <div className={styles.formActions}>
                <Button size="sm" type="submit">
                  Apply
                </Button>
                {hasFilters && (
                  <Button icon="x" onClick={handleReset} destructive ghost>
                    Reset filters
                  </Button>
                )}
              </div>
            );
          }}
        </form.Subscribe>
      </div>

      <LaneSearchModal
        isOpen={activeSearchModal === 'laneKey'}
        setIsOpen={(isOpen) => !isOpen && setActiveSearchModal(null)}
        onSelectionChange={onLaneSelectionChange}
      />

      <ProductSearchModal
        isOpen={activeSearchModal === 'productDescription'}
        setIsOpen={(isOpen) => !isOpen && setActiveSearchModal(null)}
        onSelectionChange={onProductSelectionChange}
      />
    </form>
  );
};
